import { WaterBody, type VesselProfile } from '@deckee/utilities';
import { DateTime } from 'luxon';
import {
  buildBoatingForecastRiskPoints,
  getMorningAndNightRiskLevelsForDate,
  type BoatingForecastSeries,
  type WeatherForecastRiskLevel,
} from '../risk/riskLevels';
import { normalizeWeatherForecastDayDate } from '../day/dayQuery';
import { formatWeatherForecastDayLabel } from '../day/formatDayLabel';
import { FREE_FORECAST_DAYS } from '../forecastHorizon';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const normalizeOutlookDateInTimeZone = (
  rawDate: string,
  timeZone: string,
): string => {
  if (DATE_ONLY_PATTERN.test(rawDate)) {
    return rawDate;
  }

  const parsedInZone = DateTime.fromISO(rawDate, { zone: timeZone });
  if (parsedInZone.isValid) {
    return parsedInZone.toISODate() ?? rawDate;
  }

  return normalizeWeatherForecastDayDate(rawDate) ?? rawDate;
};

export interface WeatherOutlookApiDay {
  date?: string;
  weatherIcon?: string;
  temperature?: {
    minimum?: number;
    maximum?: number;
  };
}

export interface WeatherOutlookApiResponse {
  weatherLocation?: {
    timeZone?: string;
  };
  outlook?: WeatherOutlookApiDay[];
}

export interface WeatherForecastDay {
  date: string;
  dayLabel: string;
  isToday: boolean;
  isWeekend: boolean;
  weatherIcon?: string;
  minimumTemperature?: number;
  maximumTemperature?: number;
  morningRiskLevel: WeatherForecastRiskLevel;
  nightRiskLevel: WeatherForecastRiskLevel;
}

export const formatWeatherForecastDays = (
  data: WeatherOutlookApiResponse | null | undefined,
  vessel: VesselProfile,
  waterBody: WaterBody,
  series: BoatingForecastSeries,
  maxDays = FREE_FORECAST_DAYS,
): WeatherForecastDay[] => {
  const timeZone = data?.weatherLocation?.timeZone;

  if (!timeZone || !data?.outlook?.length) {
    return [];
  }

  const riskPoints = buildBoatingForecastRiskPoints(vessel, waterBody, series);
  const today = DateTime.now().setZone(timeZone).startOf('day');

  return data.outlook.slice(0, maxDays).map((forecastDay) => {
    const rawDate = forecastDay?.date ?? '';
    const date = normalizeOutlookDateInTimeZone(rawDate, timeZone);
    const dt = DateTime.fromISO(date, { zone: timeZone });
    const isToday = dt.hasSame(today, 'day');
    const { morningRiskLevel, nightRiskLevel } =
      getMorningAndNightRiskLevelsForDate(riskPoints, date, timeZone);

    return {
      date,
      dayLabel: formatWeatherForecastDayLabel(dt, isToday),
      isToday,
      isWeekend: dt.weekday >= 6,
      weatherIcon: forecastDay?.weatherIcon,
      minimumTemperature: forecastDay?.temperature?.minimum,
      maximumTemperature: forecastDay?.temperature?.maximum,
      morningRiskLevel,
      nightRiskLevel,
    };
  });
};
